import { classNames } from '@utils/classNames';
import { highlightNeedlesInHayStack } from '@src/utils/highlightNeedlesInHayStack';
import { splitMatch } from '@src/hooks/input/combobox/useMatchedOptions';
import { useCallback, useEffect } from 'react';

export type InputOption = {
  label: string;
};

type OptionsForInputProps<T extends InputOption> = {
  options: T[];
  favorites?: T[];
  searchQuery: string;
  onOptionSelected: (selected: T) => void;
  focusedIndex?: number;
  setFocusedIndex?: (index: number) => void;
  className?: string;
};

export function OptionsForInput<T extends InputOption>({
  options,
  favorites,
  searchQuery,
  onOptionSelected,
  focusedIndex,
  setFocusedIndex,
  className,
}: OptionsForInputProps<T>) {
  const handleSelect = useCallback(
    (option: T) => {
      onOptionSelected(option);
    },
    [onOptionSelected],
  );

  useEffect(() => {
    const optionElement = document.getElementById(`option-${focusedIndex}`);
    if (optionElement?.scrollIntoView) {
      optionElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedIndex]);

  return (
    <div
      className={classNames(
        'absolute mt-1 z-55 w-full rounded-xl py-3 px-4 bg-white text-base drop-shadow-md border-blue-50 border focus:outline-none max-h-100 overflow-y-auto',
        className,
      )}
    >
      {favorites && (
        <div className="border-b border-gray-300">
          <ul data-testid="options-list-favorites">
            {favorites.map((option, index) => {
              const id = option.label.replace(/ /g, '-');
              return (
                <li
                  data-testid={`favorite-${id}`}
                  key={`favorite-${id}-${index}`}
                  id={`option-${index}`}
                  className={`relative cursor-pointer select-none rounded-md py-3 px-4 hover:bg-gray-200 ${
                    focusedIndex === index ? 'bg-gray-200' : ''
                  }`}
                  onClick={() => handleSelect(option)}
                  onMouseOver={() => setFocusedIndex?.(index)}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <ul
        data-testid={`options-list-${searchQuery.replace(/ /g, '-')}`}
        onMouseLeave={() => setFocusedIndex?.(-1)}
      >
        {options.map((option, index) => {
          const indexWithFavorites = index + (favorites?.length ?? 0);
          const id = option.label.replace(/ /g, '-');
          return (
            <li
              data-testid={`option-${id}`}
              id={`option-${indexWithFavorites}`}
              key={`option-${id}-${indexWithFavorites}`}
              className={`relative cursor-pointer select-none rounded-md py-3 px-4 hover:bg-gray-200 ${
                focusedIndex === indexWithFavorites ? 'bg-gray-200' : ''
              }`}
              onClick={() => handleSelect(option)}
              onMouseOver={() => setFocusedIndex?.(indexWithFavorites)}
              onMouseDown={(event) => event.preventDefault()}
            >
              {highlightNeedlesInHayStack(
                searchQuery,
                splitMatch(option.label, searchQuery),
                false,
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
